// src/features/operational/OperationalLayer.js
import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  MenuBook as SubjectIcon,
  Person as PersonalIcon,
  People as LearnerIcon,
  Menu as MenuIcon,
  AssignmentTurnedIn as AssessmentIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { THEME_COLORS } from '../../styles/theme';
import Dashboard from './components/Dashboard';
import SubjectManagement from './components/subject/SubjectManagement';
import LessonManagement from './components/teaching/LessonManagement';
import Assessment from './components/assessment/Assessment'; // Import the new Assessment component


// Create placeholder components for Personal and Learner Management
const PersonalManagement = ({ userId }) => (
  <Box p={4} textAlign="center">
    <Typography variant="h6">Personal Management</Typography>
    <Typography color="textSecondary">Coming soon</Typography>
  </Box>
);

const LearnerManagement = ({ userId }) => (
  <Box p={4} textAlign="center">
    <Typography variant="h6">Learner Management</Typography>
    <Typography color="textSecondary">Coming soon</Typography>
  </Box>
);

const OperationalLayer = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const { userProfile, currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  
  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const renderContent = () => {
    const userId = currentUser?.uid;
    
    switch (activeSection) {
      case 'dashboard':
        return <Dashboard userId={userId} />;
      case 'subject':
        return <SubjectManagement userId={userId} />;
      case 'teaching':
        return <LessonManagement userId={userId} />;
      case 'assessment': // Add new case for Assessment
       return <Assessment userId={userId} />;
      case 'personal':
        return <PersonalManagement userId={userId} />;
      case 'learner':
        return <LearnerManagement userId={userId} />;
      default:
        return <Dashboard userId={userId} />;
    }
  };
  
  const drawerWidth = 280;
  
  const menu = (
    <List>
      <ListItem sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
          Teaching & Learning
        </Typography>
      </ListItem>
      <Divider sx={{ mb: 2 }} />
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'dashboard'}
          onClick={() => handleSectionChange('dashboard')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <DashboardIcon sx={{ color: activeSection === 'dashboard' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Dashboard" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'dashboard' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'subject'}
          onClick={() => handleSectionChange('subject')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <SubjectIcon sx={{ color: activeSection === 'subject' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Subject Management" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'subject' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'teaching'}
          onClick={() => handleSectionChange('teaching')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <SubjectIcon sx={{ color: activeSection === 'teaching' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Lesson Management" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'teaching' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>

      {/* Add Assessment Menu Item */}
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'assessment'}
          onClick={() => handleSectionChange('assessment')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <AssessmentIcon sx={{ color: activeSection === 'assessment' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Assessment Management" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'assessment' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={activeSection === 'personal'}
          onClick={() => handleSectionChange('personal')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <PersonalIcon sx={{ color: activeSection === 'personal' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Personal Management" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'personal' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          selected={activeSection === 'learner'}
          onClick={() => handleSectionChange('learner')}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(39, 35, 97, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(39, 35, 97, 0.12)'
              }
            }
          }}
        >
          <ListItemIcon>
            <LearnerIcon sx={{ color: activeSection === 'learner' ? THEME_COLORS.primary : 'inherit' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Learner Management" 
            primaryTypographyProps={{
              fontWeight: activeSection === 'learner' ? 'bold' : 'normal'
            }}
          />
        </ListItemButton>
      </ListItem>
    </List>
  );
  
  return (
    <Container 
      maxWidth="lg" 
      sx={{ mt: 0, mb: 2, p: { xs: 0, sm: 2 } }} 
      disableGutters={isMobile}
    >
      <Box sx={{ display: 'flex' }}>
        {/* Sidebar for non-mobile */}
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: { sm: drawerWidth },
              flexShrink: { sm: 0 },
              transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                width: drawerOpen ? drawerWidth : 72,
                maxHeight: '100%',
                p: 2,
                overflow: 'hidden',
                borderRadius: 2,
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }}
            >
              {drawerOpen ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
                    Operational
                  </Typography>
                  <IconButton onClick={toggleDrawer}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" mb={2}>
                  <IconButton onClick={toggleDrawer}>
                    <MenuIcon />
                  </IconButton>
                </Box>
              )}
              
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'dashboard'}
                    onClick={() => handleSectionChange('dashboard')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <DashboardIcon sx={{ color: activeSection === 'dashboard' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Dashboard" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'dashboard' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'subject'}
                    onClick={() => handleSectionChange('subject')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <SubjectIcon sx={{ color: activeSection === 'subject' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Subject Management" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'subject' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'teaching'}
                    onClick={() => handleSectionChange('teaching')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <SubjectIcon sx={{ color: activeSection === 'teaching' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Lesson Management" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'teaching' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Add Assessment Menu Item */}
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'assessment'}
                    onClick={() => handleSectionChange('assessment')}
                    sx={{
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      <AssessmentIcon sx={{ color: activeSection === 'assessment' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Assessment Management" 
                      primaryTypographyProps={{
                        fontWeight: activeSection === 'assessment' ? 'bold' : 'normal'
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={activeSection === 'personal'}
                    onClick={() => handleSectionChange('personal')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <PersonalIcon sx={{ color: activeSection === 'personal' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Personal Management" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'personal' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={activeSection === 'learner'}
                    onClick={() => handleSectionChange('learner')}
                    sx={{
                      borderRadius: 1,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '&:hover': {
                          bgcolor: 'rgba(39, 35, 97, 0.12)'
                        }
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: drawerOpen ? 56 : 'auto',
                        mr: drawerOpen ? 'auto' : 'auto',
                      }}
                    >
                      <LearnerIcon sx={{ color: activeSection === 'learner' ? THEME_COLORS.primary : 'inherit' }} />
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText 
                        primary="Learner Management" 
                        primaryTypographyProps={{
                          fontWeight: activeSection === 'learner' ? 'bold' : 'normal'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          </Box>
        )}
        
        {/* Mobile drawer */}
        {isMobile && (
          <>
            <Drawer
              variant="temporary"
              open={drawerOpen}
              onClose={toggleDrawer}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  p: 2
                },
              }}
            >
              {menu}
            </Drawer>
            
            <Fab
              color="primary"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1100,
                bgcolor: THEME_COLORS.primary,
                '&:hover': {
                  bgcolor: '#1e1c4e'
                }
              }}
            >
              <MenuIcon />
            </Fab>
          </>
        )}
        
        {/* Main content */}
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 0 },
            pl: { sm: 3 },
            width: { xs: '100%', sm: `calc(100% - ${drawerOpen ? drawerWidth : 72}px)` },
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch', // Make sure children stretch to fill width
            minWidth: 0 // Prevent overflow issues with flexbox
          }}
        >
          {/* Main content area */}
          <Box sx={{ width: '100%', flexGrow: 1 }}>
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default OperationalLayer;